import { Copy, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useChatChannelInstallLink, useChatChannels } from "@/hooks/agent/useChatChannels";
import { Agent } from "@/types/agent/agent";
import { ChatCode, CheatSheetCard, PRIMARY_BUTTON, SectionHeading, WorkspaceRow } from "./ChatChannelShared";

interface TeamsAccessSectionProps {
  genie: Agent;
}

export default function TeamsAccessSection({ genie }: TeamsAccessSectionProps) {
  const { data: installs = [], isLoading } = useChatChannels(genie.id);
  const { data: installLink, isLoading: isLinkLoading } = useChatChannelInstallLink(genie.id, "teams");
  const teamsInstalls = installs.filter((install) => install.platform === "teams");

  const handleCopyLink = async () => {
    if (!installLink) return;
    try {
      await navigator.clipboard.writeText(installLink);
      toast.success("Link copied. Each person who opens it gets the genie in their Teams");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleOpenLink = () => {
    if (installLink) window.open(installLink, "_blank", "noopener,noreferrer");
  };

  const copyIcon = isLinkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />;
  const isComingSoon = !isLinkLoading && !installLink;

  return (
    <div className="pt-4 space-y-6">
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      ) : teamsInstalls.length === 0 ? (
        <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[10px] bg-white p-5">
            <p className="font-semibold text-gray-900">
              {isComingSoon ? "Answering in Microsoft Teams is coming soon" : "Answer customers in their Microsoft Teams"}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {isComingSoon
                ? `${genie.name} will be able to chat with customers in Teams as soon as Help Genie is available in the Teams store.`
                : `Send this link to a customer, or open it to add ${genie.name} to your own Teams. They sign in with Microsoft and ${genie.name} is waiting in their Teams chat.`}
            </p>
            <p className="text-sm font-semibold text-gray-900 mt-2">
              For businesses only: this needs a work or school Microsoft account. Personal Teams accounts can't connect.
            </p>
            {!isComingSoon && (
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <Button onClick={handleCopyLink} disabled={!installLink} className={PRIMARY_BUTTON}>
                  {copyIcon}
                  Copy Teams link
                </Button>
                <Button
                  variant="outline"
                  onClick={handleOpenLink}
                  disabled={!installLink}
                  className="min-h-[44px] font-semibold px-6"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in Teams
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <SectionHeading>Connected organizations ({teamsInstalls.length})</SectionHeading>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button size="sm" onClick={handleCopyLink} disabled={!installLink} className={PRIMARY_BUTTON}>
                {copyIcon}
                Copy Teams link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleOpenLink}
                disabled={!installLink}
                className="min-h-[44px] font-semibold"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in Teams
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            {teamsInstalls.map((install) => (
              <WorkspaceRow
                key={install.id}
                agentId={genie.id}
                install={install}
                platformLabel="Teams"
                fallbackName="Teams organization"
                removeHint="People there can remove the Help Genie app from Teams themselves."
              />
            ))}
          </div>
        </div>
      )}

      <TeamsRules genieName={genie.name} />
    </div>
  );
}

function TeamsRules({ genieName }: { genieName: string }) {
  return (
    <div>
      <div className="mb-3">
        <SectionHeading>How {genieName} works in Teams</SectionHeading>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <CheatSheetCard title="Businesses only">
          {genieName} works with organizations on Microsoft 365. Everyone needs a work or school account, and personal Teams
          accounts (like Outlook.com or Hotmail) can't connect.
        </CheatSheetCard>
        <CheatSheetCard title="Each person connects">
          Unlike Slack, Teams adds the app one person at a time. Everyone who should chat with {genieName} opens your
          link once, unless their Teams admin adds the app for everyone.
        </CheatSheetCard>
        <CheatSheetCard title="Personal chat">
          The best place to talk: open <strong>Help Genie</strong> in Teams and ask anything.
        </CheatSheetCard>
        <CheatSheetCard title="Channels and group chats">
          Someone adds Help Genie there first. Then <strong>every message</strong> needs{" "}
          <ChatCode>@Help Genie</ChatCode>, including follow-ups, because Teams only shows the app messages that mention it.
        </CheatSheetCard>
        <CheatSheetCard title="Admin approval">
          Some organizations require their Microsoft or Teams admin to approve Help Genie once, or to unblock apps. Everyone
          else there can then connect as usual.
        </CheatSheetCard>
        <CheatSheetCard title="Several genies">
          Teams asks "Who should answer?" when it isn't clear. Type <ChatCode>genies</ChatCode> to choose, or start a message
          with <ChatCode>{genieName}, …</ChatCode>. Replies show the genie's name on the message, while the app is always
          called Help Genie.
        </CheatSheetCard>
      </div>
    </div>
  );
}
